/* eslint-disable class-methods-use-this */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-image/bulbimagecroptoolbar.js
 */

import {
  Plugin, isWidget, WidgetToolbarRepository, type ViewElement,
} from 'ckeditor5';

import { isImageBeingCropped, } from '../../bulb-image-carousel/bulbimagecarouselutils';
import { isObject } from 'lodash';
import { BULB_IMAGE_CROP } from '../../../editorcustomconfig.constant';

/**
 * The image crop toolbar plugin. It creates a toolbar for the croppable image state that appears when a user initiates
 * an image crop.
 *
 * @extends Plugin
 */
export default class BulbImageCropToolbar extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [WidgetToolbarRepository];
  }

  /**
   * @inheritDoc
   */
  public static get pluginName(): string {
    return 'BulbImageCropToolbar' as const;
  }

  /**
   * @inheritDoc
   */
  public afterInit(): void {
    const { editor } = this;
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);

    widgetToolbarRepository.register('bulbImageCrop', {
      items: this.normalizeDeclarativeConfig(BULB_IMAGE_CROP.toolbar || []),
      getRelatedElement: this.getSelectedMediaViewWidget,
    });
  }

  /**
   * Convert the dropdown definitions to their keys registered in the ComponentFactory.
   * The registration precess should be handled by the plugin which handles the UI of a particular feature.
   */
  private normalizeDeclarativeConfig(config) {
    return config.map((item: { name: any; }) => (isObject(item) ? item.name : item));
  }

  /**
   * Method that delegates whether to show the bulb image crop toolbar or not.
   *
   * @param selection
   * @returns {null|module:engine/model/element~ModelElement|module:engine/view/element~ModelElement}
   * @private
   */
  private getSelectedMediaViewWidget(selection): ViewElement {
    const selectionPosition = selection.getFirstPosition();
    if (!selectionPosition) {
      return null;
    }
    const viewElement = selection.getSelectedElement();

    if (viewElement && (!!viewElement.getCustomProperty('imageCarousel') || !!viewElement.getCustomProperty('image'))
        && isWidget(viewElement) && isImageBeingCropped.value) {
      return viewElement;
    }
    let { parent } = selectionPosition;
    while (parent) {
      if (parent.is('element') && isWidget(parent)) {
        return parent;
      }
      parent = parent.parent;
    }

    return null;
  }
}
